import PropTypes from 'prop-types'
import { toast } from 'react-toastify'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import ProfileAvatar from '../../common/avatar/ProfileAvatar'

import { ReactComponent as PersonIcon } from '../../../assets/images/common/icons/person-icon.svg'
import { ReactComponent as FamilyIcon } from '../../../assets/images/common/icons/family-icon.svg'
import { ReactComponent as CallIcon } from '../../../assets/images/common/icons/call-icon.svg'
import { ReactComponent as MailIcon } from '../../../assets/images/common/icons/mail-icon.svg'
import { ReactComponent as CopyIcon } from '../../../assets/images/common/icons/copy-icon.svg'

import { GENDER_LABEL_KEYS } from '../../../utility/userHelper'
import { formatPhoneForLinks } from '../../../utility/phoneHelper'

import classes from './DetailsDrawer.module.scss'
import colors from '../../../assets/styles/globals.scss'

// The drawer's original content, now the "Contact Info" tab: quick actions plus the family admin's
// contact details.
const MemberContactTab = ({ student, familyAdmin }) => {
  const navigate = useNavigate()
  const { t } = useTranslation()

  const adminGenderInitial = t(GENDER_LABEL_KEYS[familyAdmin?.gender]).at(0)

  const handleCopy = (e, url) => {
    e.preventDefault()
    e.stopPropagation()
    navigator.clipboard.writeText(url)
    toast.success(t('members.detailsDrawer.copiedEmail'))
  }

  return (
    <>
      <div className={classes.quickActions}>
        <h6 className='font-weight-bold mb-0'>{t('members.detailsDrawer.quickActions')}</h6>
        <button
          className={classes.quickButton}
          onClick={() => navigate(`/profile/${student.id}`)}
        >
          <PersonIcon width={22} height={22} color={colors.secondaryColor} />
          {t('common.action.viewProfile')}
        </button>
        <button
          className={classes.quickButton}
          onClick={() => navigate(`/members/families/${student.family.id}`)}
        >
          <FamilyIcon width={22} height={22} color={colors.secondaryColor} />
          {t('members.detailsDrawer.viewFamilyPage')}
        </button>
      </div>
      {familyAdmin && (
        <div className={classes.contact}>
          <h6 className='font-weight-bold mb-0'>{t('members.contactFamilyAdmin')}</h6>
          <div className={classes.profile}>
            <ProfileAvatar student={familyAdmin} />
            <div className={classes.info}>
              <div className={classes.name}>
                <span>{familyAdmin.firstName} {familyAdmin.lastName}</span>
                <span className={classes.familyAdminLabel}>{t('members.familyAdmin')}</span>
              </div>
              <span>
                {t('members.ageGenderLabel', { age: familyAdmin.age ?? '-', gender: adminGenderInitial })}
              </span>
            </div>
          </div>
          {familyAdmin.phonePrefix && familyAdmin.phone && (
            <a
              className={classes.phoneButton}
              href={familyAdmin.phone ? `tel:${formatPhoneForLinks(familyAdmin.phonePrefix, familyAdmin.phone)}` : '#'}
            >
              <CallIcon className={classes.contactIcon} />
              {familyAdmin.phonePrefix} {familyAdmin.phone || ''}
            </a>
          )}
          <a
            href={familyAdmin.email ? `mailto:${familyAdmin.email}` : '#'}
            className={classes.mailButton}
          >
            <MailIcon className={classes.contactIcon} />
            <div className={classes.emailWrapper}>
              <button
                onClick={(e) => handleCopy(e, familyAdmin.email)}
                className={classes.copyButton}
                aria-label={t('members.detailsDrawer.copyEmailAria')}
              >
                <CopyIcon className={classes.copyIcon} />
              </button>
              {familyAdmin.email || '-'}
            </div>
          </a>
        </div>
      )}
    </>
  )
}

MemberContactTab.propTypes = {
  student: PropTypes.object.isRequired,
  familyAdmin: PropTypes.object,
}

export default MemberContactTab
